import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  createStyles,
} from "@mantine/core";
import { DateInput, DateTimePicker, TimeInput } from "@mantine/dates";
import { pdf } from "@react-pdf/renderer";
import { IconDownload, IconFileTypePdf } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import moment from "moment";
import { useSelector } from "react-redux";
import IncidentReportPDF from "../IncidentReportPDF";
import { getNoteSettings } from "../../services/noteSettings";
import { decrypt } from "../../utils";

const useStyles = createStyles(() => ({
  modal: {
    "& .mantine-Modal-content": {
      background: "#ffffff",
      borderRadius: 20,
      maxHeight: "90vh",
      border: "1px solid #ece4d8",
    },
    "& .mantine-Modal-header": {
      background: "transparent",
      paddingBottom: 0,
      paddingTop: 18,
      paddingLeft: 22,
      paddingRight: 22,
    },
    "& .mantine-Modal-body": {
      padding: "8px 22px 22px",
      maxHeight: "calc(90vh - 64px)",
      overflowY: "auto",
    },
  },
  shell: {
    padding: "2px 0 8px",
    maxWidth: 1120,
    margin: "0 auto",
  },
  hero: {
    background: "#ffffff",
    border: "1px solid #e8dfd1",
    borderRadius: 18,
    padding: 24,
    marginBottom: 16,
  },
  heroTitle: {
    color: "#202124",
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: 8,
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: "#6f6a62",
    fontSize: 15,
    alignItems: "center",
  },
  dot: {
    color: "#c0b5a5",
  },
  section: {
    background: "#ffffff",
    border: "1px solid #e8dfd1",
    borderRadius: 18,
    overflow: "hidden",
    marginBottom: 16,
  },
  sectionHeader: {
    background: "#efebe3",
    color: "#202124",
    fontSize: 17,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    padding: "16px 18px",
    borderBottom: "1px solid #e2dacd",
  },
  sectionBody: {
    padding: 18,
  },
  inlineChecks: {
    display: "flex",
    gap: 18,
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 18,
    alignItems: "center",
    flexWrap: "wrap",
  },
  downloadButton: {
    height: 40,
    padding: "0 16px",
    borderRadius: 10,
    border: "1px solid #d7dce5",
    background: "#ffffff",
    color: "#26303c",
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },
  statusToggleRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  statusToggle: {
    borderRadius: 999,
    padding: "10px 18px",
    border: "1px solid #dfd5c4",
    color: "#8a8680",
    background: "#f8f4ec",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  signaturePreview: {
    border: "1px solid #e3d9ca",
    borderRadius: 12,
    background: "#fbfaf8",
    minHeight: 86,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "8px 12px",
  },
  signatureImage: {
    width: "100%",
    height: 68,
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  signatureText: {
    fontSize: 24,
    lineHeight: 1,
    color: "#2d3138",
  },
}));

const extractStaffName = (value = "") => {
  if (!value) return "";
  return value.split(" - ")[0]?.trim() || value;
};

const safeDecrypt = (value) => {
  if (!value) return null;
  try {
    return decrypt(value) || null;
  } catch {
    return value;
  }
};

const formatTimeValue = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm", "hh:mm A", moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("HH:mm") : "";
};

const mergeDateAndTime = (dateValue, timeValue) => {
  const base = moment(dateValue || new Date());
  if (!base.isValid()) return new Date();
  const time = moment(timeValue, ["HH:mm", "hh:mm A"], true);
  if (time.isValid()) {
    base.hour(time.hour());
    base.minute(time.minute());
    base.second(0);
    base.millisecond(0);
  }
  return base.toDate();
};

const INCIDENT_LOCATIONS = [
  "Resident bedroom",
  "Resident bathroom",
  "Resident closet / dressing area",
  "Hallway",
  "Common living room",
  "Dining room",
  "Kitchen / kitchenette",
  "Staff office",
  "Medication room",
  "Laundry room",
  "Storage room",
  "Stairwell",
  "Elevator",
  "Lobby / entrance area",
  "Visitor area / waiting room",
  "Activity room",
  "Front entrance / walkway",
  "Parking lot / driveway",
  "Patio / courtyard",
  "Garden / outdoor seating area",
  "Backyard",
  "Side yard / pathway",
  "TV / lounge area",
  "Chapel / meditation room",
  "Unknown — resident found after the fact",
  "Other — specify",
];

const IncidentReportModal = ({
  opened,
  onClose,
  clientDetails,
  profile,
  emsData,
}) => {
  const { classes } = useStyles();
  const { allStaffWithSignature } = useSelector((state) => state?.staff);
  const { noteSettings } = useSelector((state) => state?.note);

  const [form, setForm] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Org-level "Other providers" configured in Note Settings — load them when
  // the modal opens if they aren't already in the store.
  useEffect(() => {
    if (opened && !noteSettings) getNoteSettings();
  }, [opened, noteSettings]);

  const facilityName = useMemo(
    () =>
      profile?.business?.companyName ||
      clientDetails?.business?.companyName ||
      profile?.facility?.name ||
      "Facility",
    [profile, clientDetails]
  );

  useEffect(() => {
    if (!opened || !clientDetails) return;

    const now = moment();
    const nowDate = now.toDate();
    const staffName =
      extractStaffName(emsData?.staffAndDate) || profile?.user?.name || "";
    const pcpName = clientDetails?.form2?.pcpName || "";
    const repName =
      emsData?.authorizedRepresentative ||
      clientDetails?.form2?.emergencyContactName ||
      "";
    const defaultStaff =
      allStaffWithSignature?.find((staff) => staff?.name === staffName) ||
      allStaffWithSignature?.find((staff) => staff?._id === profile?.user?.id) ||
      null;

    setForm({
      incidentDate: nowDate,
      incidentTime: nowDate,
      incidentLocation: facilityName,
      residentsInvolved: clientDetails?.name ? [clientDetails.name] : [],
      staffInvolved: staffName ? [staffName] : [],
      narrative: emsData?.emergencyNotes || "",
      physicianOrHospitalVisit: emsData?.didNotGoToHospital ? false : true,
      physicianHospitalName: emsData?.didNotGoToHospital
        ? ""
        : emsData?.hospitalDestination || pcpName,
      physicianHospitalAddress: "",
      physicianHospitalPhone: "",
      transportedDateTime: nowDate,
      notTransported: emsData?.didNotGoToHospital || false,
      transportedHow: emsData?.didNotGoToHospital ? "" : "Ambulance",
      policeInvolved: false,
      officerName: "",
      badgeNumber: "",
      reportNumber: "",
      authorizedRepName: repName,
      authorizedRepDateTime: nowDate,
      pcpName,
      pcpDateTime: null,
      otherNotifiedName: "",
      otherNotifiedDateTime: null,
      selectedProviders: [],
      adhsNotified: false,
      actualHarmOccurred: false,
      potentialForHarm: true,
      residentConditionChange: "",
      actionTakenImmediately: emsData?.emergencyNotes || "",
      actionTakenPreventRecurrence: "",
      abuseNeglectSuspected: false,
      suspectedIncidentDateTime: null,
      suspectedIncidentDescription: "",
      witnessNames: staffName,
      investigationPreventionActions: "",
      completedById: defaultStaff?._id || profile?.user?.id || "",
      completedBy: defaultStaff?.name || profile?.user?.name || "",
      signatureData: defaultStaff?.signature || null,
      dateCompleted: nowDate,
    });
  }, [opened, clientDetails, profile, emsData, facilityName, allStaffWithSignature]);

  // Providers offered for selection: the client's own Care & Support providers
  // plus the org-wide providers configured in Note Settings. Staff pick which
  // ones were notified; the selected entries are carried into the report PDF.
  const clientProviders = useMemo(() => {
    // Normalize a raw provider record (from either the client's Care & Support
    // list or the org-level Note Settings list) into a selectable entry.
    const normalizeProvider = (p, keyPrefix, index) => {
      const name =
        `${p?.providerFirstName || ""} ${p?.providerLastName || ""}`.trim() ||
        p?.providerName ||
        p?.name ||
        "";
      const type =
        p?.providerType === "Other"
          ? p?.providerOtherType || ""
          : p?.providerType || p?.role || "";
      return {
        key: `${keyPrefix}:${p?.id ?? index}`,
        source: keyPrefix,
        name,
        type,
        phone: p?.providerPhone || "",
        email: p?.providerEmail || "",
      };
    };

    const clientList = Array.isArray(clientDetails?.form2?.additionalProvider)
      ? clientDetails.form2.additionalProvider.map((p, i) =>
          normalizeProvider(p, "client", i)
        )
      : [];
    const orgList = Array.isArray(noteSettings?.incident?.otherProviders)
      ? noteSettings.incident.otherProviders.map((p, i) =>
          normalizeProvider(p, "org", i)
        )
      : [];
    return [...clientList, ...orgList].filter(
      (p) => p.name || p.type || p.phone || p.email
    );
  }, [clientDetails, noteSettings]);

  if (!clientDetails) return null;

  const updateField = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const isProviderSelected = (key) =>
    (form.selectedProviders || []).some((p) => p.key === key);

  const toggleProvider = (provider) =>
    setForm((current) => {
      const selected = current.selectedProviders || [];
      const exists = selected.some((p) => p.key === provider.key);
      return {
        ...current,
        selectedProviders: exists
          ? selected.filter((p) => p.key !== provider.key)
          : [...selected, provider],
      };
    });

  const staffOptions = (allStaffWithSignature || []).map((staff) => ({
    value: staff?.name || "",
    label: staff?.name || "",
  }));

  const residentOptions = clientDetails?.name
    ? [{ value: clientDetails.name, label: clientDetails.name }]
    : [];

  const completedByOptions = (allStaffWithSignature || []).map((staff) => ({
    value: staff?._id || "",
    label: staff?.name || "",
  }));
  const signatureImageSrc =
    safeDecrypt(form?.signatureData?.signature) ||
    safeDecrypt(form?.signatureData?.signatureImage) ||
    safeDecrypt(form?.signatureData?.signatureInitials) ||
    null;
  const signatureText =
    form?.signatureData?.signatureText ||
    form?.signatureData?.signatureInitialsText ||
    "";

  const handleDownload = async () => {
    if (!clientDetails) return;
    setDownloadLoading(true);
    try {
      const blob = await pdf(
        <IncidentReportPDF
          clientDetails={clientDetails}
          profile={profile}
          form={form}
        />
      ).toBlob();
      const clientName = clientDetails?.name || "resident";
      saveAs(
        blob,
        `${clientName.replace(/\s+/g, "_")}_Incident_Report_${moment().format("YYYY-MM-DD")}.pdf`
      );
      onClose?.();
    } catch (error) {
      console.error("Incident report PDF generation error:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Incident Report"
      size="72rem"
      centered
      overlayProps={{ blur: 3 }}
      className={classes.modal}
      zIndex={1000}
      shadow="md"
      radius="lg"
      
    >
      <Box className={classes.shell}>
        <Box className={classes.hero}>
          <Text className={classes.heroTitle}>Incident Report</Text>
          <Box className={classes.heroMeta}>
            <span>{clientDetails?.name || "Resident"}</span>
            <span className={classes.dot}>·</span>
            <span>{facilityName}</span>
            <span className={classes.dot}>·</span>
            <span>{`Incident date: ${form.incidentDate ? moment(form.incidentDate).format("MM/DD/YYYY") : "—"}`}</span>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Incident Identification</Box>
          <Box className={classes.sectionBody}>
            <Grid gutter={16}>
              <Grid.Col span={6}>
                <DateInput
                  label="Date of incident"
                  value={form.incidentDate || null}
                  valueFormat="MM/DD/YYYY"
                  onChange={(value) => {
                    updateField("incidentDate", value);
                    updateField(
                      "incidentTime",
                      mergeDateAndTime(value, formatTimeValue(form.incidentTime))
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TimeInput
                  label="Time of incident"
                  value={formatTimeValue(form.incidentTime)}
                  onChange={(event) =>
                    updateField(
                      "incidentTime",
                      mergeDateAndTime(form.incidentDate, event.currentTarget.value)
                    )
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Select
                  label="Location of incident"
                  data={INCIDENT_LOCATIONS}
                  value={form.incidentLocation || ""}
                  onChange={(value) => updateField("incidentLocation", value || "")}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiSelect
                  label="Resident(s) involved"
                  data={residentOptions}
                  value={form.residentsInvolved || []}
                  onChange={(value) => updateField("residentsInvolved", value)}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiSelect
                  label="Staff members involved, including witnesses"
                  data={staffOptions}
                  value={form.staffInvolved || []}
                  onChange={(value) => updateField("staffInvolved", value)}
                  searchable
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Narrative</Box>
          <Box className={classes.sectionBody}>
            <Textarea
              label="Description of the incident"
              placeholder="Cover who, what, when, where, how, and why"
              minRows={5}
              value={form.narrative || ""}
              onChange={(event) => updateField("narrative", event.currentTarget.value)}
            />
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Injury / Medical Response</Box>
          <Box className={classes.sectionBody}>
            <Stack spacing={14}>
              <Box className={classes.inlineChecks}>
                <Checkbox
                  label="Injury required physician or hospital visit"
                  checked={Boolean(form.physicianOrHospitalVisit)}
                  onChange={(event) =>
                    updateField("physicianOrHospitalVisit", event.currentTarget.checked)
                  }
                  color="yellow"
                />
                <Checkbox
                  label="Police involved"
                  checked={Boolean(form.policeInvolved)}
                  onChange={(event) =>
                    updateField("policeInvolved", event.currentTarget.checked)
                  }
                  color="yellow"
                />
              </Box>

              <Grid gutter={16}>
                <Grid.Col span={6}>
                  <TextInput
                    label="Physician / hospital name"
                    value={form.physicianHospitalName || ""}
                    onChange={(event) =>
                      updateField("physicianHospitalName", event.currentTarget.value)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Phone number"
                    value={form.physicianHospitalPhone || ""}
                    onChange={(event) =>
                      updateField("physicianHospitalPhone", event.currentTarget.value)
                    }
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <DateTimePicker
                    label="Date / time transported"
                    value={form.transportedDateTime || null}
                    valueFormat="MM/DD/YYYY hh:mm A"
                    onChange={(value) => updateField("transportedDateTime", value)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack spacing={8}>
                    <Checkbox
                      label="Not transported"
                      checked={Boolean(form.notTransported)}
                      onChange={(event) =>
                        updateField("notTransported", event.currentTarget.checked)
                      }
                      color="yellow"
                    />
                    <TextInput
                    label="How transported"
                    value={form.transportedHow || ""}
                    disabled={Boolean(form.notTransported)}
                    onChange={(event) =>
                      updateField("transportedHow", event.currentTarget.value)
                    }
                  />
                  </Stack>
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label="Physician / hospital address"
                    value={form.physicianHospitalAddress || ""}
                    onChange={(event) =>
                      updateField("physicianHospitalAddress", event.currentTarget.value)
                    }
                  />
                </Grid.Col>
                {form.policeInvolved ? (
                  <>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Officer name"
                        value={form.officerName || ""}
                        onChange={(event) =>
                          updateField("officerName", event.currentTarget.value)
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Badge number"
                        value={form.badgeNumber || ""}
                        onChange={(event) =>
                          updateField("badgeNumber", event.currentTarget.value)
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={4}>
                      <TextInput
                        label="Report number"
                        value={form.reportNumber || ""}
                        onChange={(event) =>
                          updateField("reportNumber", event.currentTarget.value)
                        }
                      />
                    </Grid.Col>
                  </>
                ) : null}
              </Grid>
            </Stack>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Notifications</Box>
          <Box className={classes.sectionBody}>
            <Grid gutter={16}>
              <Grid.Col span={6}>
                <TextInput
                  label="Authorized representative / emergency contact"
                  value={form.authorizedRepName || ""}
                  onChange={(event) =>
                    updateField("authorizedRepName", event.currentTarget.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <DateTimePicker
                  label="Date / time notified"
                  value={form.authorizedRepDateTime || null}
                  valueFormat="MM/DD/YYYY hh:mm A"
                  onChange={(value) => updateField("authorizedRepDateTime", value)}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Primary care physician"
                  value={form.pcpName || ""}
                  onChange={(event) => updateField("pcpName", event.currentTarget.value)}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <DateTimePicker
                  label="Date / time notified"
                  value={form.pcpDateTime || null}
                  valueFormat="MM/DD/YYYY hh:mm A"
                  onChange={(value) => updateField("pcpDateTime", value)}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Other persons notified"
                  value={form.otherNotifiedName || ""}
                  onChange={(event) =>
                    updateField("otherNotifiedName", event.currentTarget.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <DateTimePicker
                  label="Date / time notified"
                  value={form.otherNotifiedDateTime || null}
                  valueFormat="MM/DD/YYYY hh:mm A"
                  onChange={(value) => updateField("otherNotifiedDateTime", value)}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Checkbox
                  label="Regulator notification required / completed"
                  checked={Boolean(form.adhsNotified)}
                  onChange={(event) => updateField("adhsNotified", event.currentTarget.checked)}
                  color="yellow"
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Providers Notified</Box>
          <Box className={classes.sectionBody}>
            {clientProviders.length === 0 ? (
              <Text size="sm" color="#8a8680">
                No providers on the client's Care &amp; Support record or in Note Settings.
              </Text>
            ) : (
              <Stack spacing={10}>
                <Text size="sm" color="#6f6a62">
                  Select the providers who were notified of this incident.
                </Text>
                {clientProviders.map((provider) => (
                  <Checkbox
                    key={provider.key}
                    color="yellow"
                    checked={isProviderSelected(provider.key)}
                    onChange={() => toggleProvider(provider)}
                    label={
                      <Box>
                        <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Text size="sm" fw={600} color="#26303c">
                            {provider.name || "Unnamed provider"}
                            {provider.type ? ` · ${provider.type}` : ""}
                          </Text>
                          <Text
                            size="xs"
                            style={{
                              padding: "1px 8px",
                              borderRadius: 999,
                              background: provider.source === "org" ? "#eef2ff" : "#f0fdf4",
                              color: provider.source === "org" ? "#3730a3" : "#166534",
                              fontWeight: 600,
                            }}
                          >
                            {provider.source === "org" ? "Org" : "Client"}
                          </Text>
                        </Box>
                        {(provider.phone || provider.email) && (
                          <Text size="xs" color="#8a8680">
                            {[provider.phone, provider.email].filter(Boolean).join(" · ")}
                          </Text>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Evaluation</Box>
          <Box className={classes.sectionBody}>
            <Stack spacing={14}>
              <Box className={classes.statusToggleRow}>
                <Box
                  className={classes.statusToggle}
                  style={
                    form.actualHarmOccurred
                      ? { background: "#f0b10a", color: "#fff", borderColor: "#f0b10a" }
                      : undefined
                  }
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      actualHarmOccurred: true,
                      potentialForHarm: false,
                    }))
                  }
                >
                  Actual harm occurred
                </Box>
                <Box
                  className={classes.statusToggle}
                  style={
                    form.potentialForHarm
                      ? { background: "#f0b10a", color: "#fff", borderColor: "#f0b10a" }
                      : undefined
                  }
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      potentialForHarm: true,
                      actualHarmOccurred: false,
                    }))
                  }
                >
                  Potential for harm exists
                </Box>
              </Box>
              <Textarea
                label="Description of injury or change in condition"
                minRows={4}
                value={form.residentConditionChange || ""}
                onChange={(event) =>
                  updateField("residentConditionChange", event.currentTarget.value)
                }
              />
            </Stack>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Actions</Box>
          <Box className={classes.sectionBody}>
            <Grid gutter={16}>
              <Grid.Col span={6}>
                <Textarea
                  label="Action taken immediately after the incident"
                  minRows={4}
                  value={form.actionTakenImmediately || ""}
                  onChange={(event) =>
                    updateField("actionTakenImmediately", event.currentTarget.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Textarea
                  label="Action taken to prevent recurrence"
                  minRows={4}
                  value={form.actionTakenPreventRecurrence || ""}
                  onChange={(event) =>
                    updateField("actionTakenPreventRecurrence", event.currentTarget.value)
                  }
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Investigation</Box>
          <Box className={classes.sectionBody}>
            <Stack spacing={14}>
              <Checkbox
                label="Abuse / neglect suspected"
                checked={Boolean(form.abuseNeglectSuspected)}
                onChange={(event) =>
                  updateField("abuseNeglectSuspected", event.currentTarget.checked)
                }
                color="yellow"
              />
              <DateTimePicker
                label="Dates and times of suspected incident"
                value={form.suspectedIncidentDateTime || null}
                valueFormat="MM/DD/YYYY hh:mm A"
                onChange={(value) => updateField("suspectedIncidentDateTime", value)}
              />
              <Textarea
                label="Description of the suspected incident"
                minRows={4}
                value={form.suspectedIncidentDescription || ""}
                onChange={(event) =>
                  updateField("suspectedIncidentDescription", event.currentTarget.value)
                }
              />
              <TextInput
                label="Names of witnesses"
                value={form.witnessNames || ""}
                onChange={(event) =>
                  updateField("witnessNames", event.currentTarget.value)
                }
              />
              <Textarea
                label="Actions taken to prevent recurrence"
                minRows={3}
                value={form.investigationPreventionActions || ""}
                onChange={(event) =>
                  updateField("investigationPreventionActions", event.currentTarget.value)
                }
              />
            </Stack>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>Sign-off</Box>
          <Box className={classes.sectionBody}>
            <Grid gutter={16}>
              <Grid.Col span={4}>
                <Select
                  label="Printed name"
                  data={completedByOptions}
                  value={form.completedById || ""}
                  onChange={(value) => {
                    const selectedStaff = (allStaffWithSignature || []).find(
                      (staff) => staff?._id === value
                    );
                    setForm((current) => ({
                      ...current,
                      completedById: value || "",
                      completedBy: selectedStaff?.name || "",
                      signatureData: selectedStaff?.signature || null,
                    }));
                  }}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Box>
                  <Text size="sm" mb={6}>
                    Signature
                  </Text>
                  <Box className={classes.signaturePreview}>
                    {signatureImageSrc ? (
                      <img
                        src={signatureImageSrc}
                        alt="signature"
                        className={classes.signatureImage}
                      />
                    ) : signatureText ? (
                      <Text className={classes.signatureText}>{signatureText}</Text>
                    ) : (
                      <Text size="sm" color="#8a8680">
                        No saved signature
                      </Text>
                    )}
                  </Box>
                </Box>
              </Grid.Col>
              <Grid.Col span={4}>
                <DateInput
                  label="Date completed"
                  value={form.dateCompleted || null}
                  valueFormat="MM/DD/YYYY"
                  onChange={(value) => updateField("dateCompleted", value)}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Box>

        <Box className={classes.actions}>
          <Button
            className={classes.downloadButton}
            variant="default"
            onClick={handleDownload}
            loading={downloadLoading}
          >
            <IconFileTypePdf size={18} stroke={1.8} />
            {downloadLoading ? "Preparing Download..." : "Download Incident Report"}
            {!downloadLoading && <IconDownload size={16} stroke={1.8} />}
          </Button>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default IncidentReportModal;
